import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Check, MapPin, ShieldCheck } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { getPlatformStatus } from '../../lib/admin-api'
import {
  getOrgSettings,
  updateOrgSettings,
  type OrgSettings,
  type UpdateOrgInput,
} from '../../lib/onboarding-api'
import { DangerZone } from './danger-zone'

const fieldClass =
  'flex h-10 w-full rounded-md border border-border bg-bg-alt px-3 py-2 text-sm text-fg ' +
  'focus-visible:outline-none focus-visible:border-secondary focus-visible:ring-[3px] focus-visible:ring-ring'

// A small, sane subset of IANA zones for the picker (free-text fallback below).
const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'Europe/London',
  'Europe/Berlin',
  'Australia/Sydney',
]

export function SettingsPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['org-settings'],
    queryFn: getOrgSettings,
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        description="Manage your organization profile, branding, and campuses."
        actions={
          <Button asChild variant="secondary">
            <Link to="/app/settings/sites">
              <MapPin />
              Manage sites
            </Link>
          </Button>
        }
      />

      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading settings…
          </div>
        </Card>
      ) : isError ? (
        <Card>
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn't load settings.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'You may not have permission.'}
            </p>
          </div>
        </Card>
      ) : data ? (
        <>
          <OrgSettingsForm
            settings={data}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ['org-settings'] })}
          />

          {/* Platform administration entry point — only shown to platform admins.
              Gated on GET /admin/platform/status; platform-admin is a flag, NOT an
              org permission, so this is intentionally not a permission-based nav. */}
          <PlatformAdminCard />

          {/* Org self-management ("Danger Zone"). The API re-checks the gate and
              derives the org from the session; the slug is only used for the
              type-to-confirm UX. */}
          <DangerZone orgSlug={data.slug} />
        </>
      ) : null}
    </div>
  )
}

/**
 * Conditionally surfaces a link to the cross-org Platform Admin page, only when
 * the signed-in user holds the platform super-admin flag (a capability ABOVE
 * orgs, not an org RBAC permission). A non-admin sees nothing.
 */
function PlatformAdminCard() {
  const { data } = useQuery({ queryKey: ['platform-status'], queryFn: getPlatformStatus })
  if (!data?.isPlatformAdmin) return null

  return (
    <Card className="max-w-3xl border-secondary/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="size-4 text-secondary" />
          Platform administration
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted">
          You have platform super-admin access. Manage every organization on this platform.
        </p>
        <Button asChild variant="secondary">
          <Link to="/app/platform">Open platform admin</Link>
        </Button>
      </CardContent>
    </Card>
  )
}

function OrgSettingsForm({ settings, onSaved }: { settings: OrgSettings; onSaved: () => void }) {
  const [name, setName] = useState(settings.name)
  const [legalName, setLegalName] = useState(settings.legalName ?? '')
  const [timezone, setTimezone] = useState(settings.timezone)
  const [locale, setLocale] = useState(settings.locale)
  const [currency, setCurrency] = useState(settings.currency)
  const [tagline, setTagline] = useState(settings.branding.tagline ?? '')
  const [logoUrl, setLogoUrl] = useState(settings.branding.logoUrl ?? '')
  const [primaryColor, setPrimaryColor] = useState(settings.branding.primaryColor ?? '')
  const [justSaved, setJustSaved] = useState(false)

  useEffect(() => {
    if (!justSaved) return
    const id = setTimeout(() => setJustSaved(false), 2500)
    return () => clearTimeout(id)
  }, [justSaved])

  const mutation = useMutation({
    mutationFn: (input: UpdateOrgInput) => updateOrgSettings(input),
    onSuccess: () => {
      setJustSaved(true)
      onSaved()
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    mutation.mutate({
      name: name.trim(),
      legalName: legalName.trim() || null,
      timezone,
      locale,
      currency: currency.trim().toUpperCase(),
      branding: {
        tagline: tagline.trim() || null,
        logoUrl: logoUrl.trim() || null,
        primaryColor: primaryColor.trim() || null,
      },
    })
  }

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="size-4 text-secondary" />
            Organization profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="org-legal">Legal name</Label>
              <Input
                id="org-legal"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="org-handle">Handle</Label>
            <Input
              id="org-handle"
              value={settings.slug}
              disabled
              readOnly
              aria-describedby="handle-note"
            />
            <p id="handle-note" className="text-xs text-dim">
              Your workspace handle is permanent and can't be changed here.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="org-tz">Timezone</Label>
              <select
                id="org-tz"
                className={fieldClass}
                value={COMMON_TIMEZONES.includes(timezone) ? timezone : '__custom'}
                onChange={(e) => {
                  if (e.target.value !== '__custom') setTimezone(e.target.value)
                }}
              >
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
                {!COMMON_TIMEZONES.includes(timezone) && (
                  <option value="__custom">{timezone}</option>
                )}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="org-locale">Locale</Label>
              <Input id="org-locale" value={locale} onChange={(e) => setLocale(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="org-currency">Currency</Label>
              <Input
                id="org-currency"
                value={currency}
                maxLength={3}
                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle>Branding</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="brand-tagline">Tagline</Label>
            <Input
              id="brand-tagline"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="A welcoming home for every family"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="brand-logo">Logo URL</Label>
              <Input
                id="brand-logo"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://…/logo.png"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="brand-primary">Primary color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  aria-label="Primary color picker"
                  value={/^#[0-9a-fA-F]{6}$/.test(primaryColor) ? primaryColor : '#6d5ef0'}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="size-10 shrink-0 cursor-pointer rounded-md border border-border bg-bg-alt"
                />
                <Input
                  id="brand-primary"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#6d5ef0"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex max-w-3xl items-center gap-3">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? <Spinner /> : null}
          Save changes
        </Button>
        {justSaved && (
          <span className="flex items-center gap-1.5 text-sm text-success" role="status">
            <Check className="size-4" /> Saved
          </span>
        )}
        {mutation.isError && (
          <span className="text-sm text-danger" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : 'Could not save.'}
          </span>
        )}
      </div>
    </form>
  )
}
